import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, } from "react";
import { Field } from "formik";
import { styled } from "../../theme";
import { Button } from "../../button";
import { FloatingFieldLabel } from "../atoms/FloatingFieldLabel";
import { FileInput } from "../atoms/file-input";
import { FieldError } from "../atoms/field-error";
import { Label } from "../atoms/Label";
import { ExternalLink } from "../../external-link";
const PRE_SIGNED_URL_REFRESH_INTERVAL = 60000; // 1 minute
/**
 * A formik field for file uploads.
 *
 * @param {FileFieldProps} props The props for the component.
 * @returns {JSX.Element} The rendered file field component.
 */
export const FileField = ({ name, label, title, emptyMessage, uploadedMessage, acceptedMediaTypes, disabled, FormField, queryVariables, }) => {
    return (_jsx(FormField, { name: name, children: ({ field, form }) => {
            const { value } = field;
            const { setFieldTouched, setFieldValue } = form;
            const onChange = (event) => {
                setFieldTouched(name, true);
                setFieldValue(name, event.currentTarget.files[0]);
            };
            const onRemove = () => {
                setFieldValue(name, undefined);
            };
            return (_jsxs(_Fragment, { children: [label && (_jsx(Label, { htmlFor: name, children: _jsx(FloatingFieldLabel, { visible: true, children: label }) })), _jsxs(Wrapper, { children: [_jsxs(FileInputTrigger, { disabled: disabled, children: [_jsx("span", { children: title }), _jsx(FileInput, { id: name, name: name, acceptedMediaTypes: acceptedMediaTypes, disabled: disabled, onChange: onChange })] }), _jsx(FileInputValue, { name: name, value: value, onRemove: onRemove, emptyMessage: emptyMessage, uploadedMessage: uploadedMessage, disabled: disabled, queryVariables: queryVariables })] }), _jsx(FieldError, { name: name })] }));
        } }));
};
FileField.defaultProps = {
    title: "Upload File",
    emptyMessage: "No file chosen",
    uploadedMessage: "Show current content",
    disabled: false,
    FormField: Field,
};
/**
 * A component that displays the value of a file input.
 *
 * @param {object} props The props for the component.
 * @returns {JSX.Element} The rendered file input value component.
 */
export const FileInputValue = ({ name, value, emptyMessage, uploadedMessage, disabled, onRemove, queryVariables, }) => {
    if (!value) {
        return _jsx("label", { htmlFor: name, children: emptyMessage });
    }
    const [preSignedUrl, setPreSignedUrl] = useState(null);
    useEffect(() => {
        const fetchFileDetails = async (url, fileId) => {
            try {
                const response = await fetch(queryVariables?.transformFileUrl
                    ? queryVariables.transformFileUrl(`/v3/files/${fileId}`)
                    : `/v3/files/${fileId}`, {
                    headers: {
                        Authorization: queryVariables?.getCommonAuthorizationHeader(),
                    },
                });
                const data = await response.json();
                setPreSignedUrl(data.url);
                return data;
            }
            catch (error) {
                const auth = queryVariables?.getCommonAuthorizationHeader?.();
                const enhancedError = new Error(`Failed to fetch pre-signed URL: ${error.message || error}. ` +
                    `Auth: ${auth ? "present" : "missing"}. ` +
                    `Query variables: ${queryVariables ? "present" : "missing"}`);
                // Works with the current TypeScript configuration without requiring lib updates
                enhancedError.cause = error;
                throw enhancedError;
            }
        };
        if (!(value instanceof File)) {
            // Call immediately
            fetchFileDetails(value?.url, value?.id);
            // Set up interval to refresh every minute (60,000 ms) for now
            const intervalId = setInterval(() => {
                fetchFileDetails(value?.url, value?.id);
            }, PRE_SIGNED_URL_REFRESH_INTERVAL);
            // Cleanup interval on unmount or dependency change
            return () => clearInterval(intervalId);
        }
    }, [value, queryVariables]);
    return (_jsxs(_Fragment, { children: [value instanceof File ? (_jsx(OverflowEllipsis, { children: value.name })) : (_jsx(ExternalLink, { href: preSignedUrl, children: uploadedMessage })), _jsx(ButtonWrapper, { children: _jsx(Button, { outline: true, color: "danger", size: "small", icon: "close", disabled: disabled, onClick: onRemove, children: "Remove" }) })] }));
};
const OverflowEllipsis = styled.span `
  width: 100%;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Wrapper = styled.div `
  display: inline-flex;
  align-items: center;

  margin-top: 15px;
  margin-bottom: 5px;

  width: 100%;
`;
const ButtonWrapper = styled.div `
  margin-left: 16px;
`;
/**
 * Note: <Button /> can't be reused here, as we are very custom.
 */
export const FileInputTrigger = styled.label `
  margin-right: 16px;
  padding: 12px 32px;
  border-radius: 5px;
  border: 1px solid ${({ theme }) => theme.color.primary};

  background-color: ${({ theme }) => theme.color.white};
  color: ${({ theme }) => theme.color.primary};
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.3);

  font-weight: ${({ theme }) => theme.fontWeight.medium};
  font-size: 15px;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.color.primary};
    color: ${({ theme }) => theme.color.white};
    transform: translateY(-1px);
  }

  ${({ disabled }) => disabled &&
    `
    opacity: 0.6;
    cursor: not-allowed;
    filter: saturate(25%);
  `}
`;
